import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { axPerfLogger } from '@theme/performance';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} Flag to prevent re-broadcasting close events from other tabs */
  #closingFromBroadcast = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // Listen for broadcast close from other tabs
    document.addEventListener('cart:drawer-close', this.#handleBroadcastClose);

    // Auto-open drawer when landing on /cart in drawer mode with redirect enabled
    if (this.hasAttribute('data-redirect-cart-page') && window.location.pathname === `${window.Shopify?.routes?.root ?? '/'}cart`) {
      this.showDialog();
    }

    // Detect all dialog close events (button, Escape, click outside)
    const { dialog } = /** @type {Refs} */ (this.refs);
    dialog?.addEventListener('close', this.#handleDialogClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // Reopen the drawer on the page the cart page redirected to (snippets/cart-page-redirect.liquid)
    if (this.#consumeReopenFlag()) {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    document.removeEventListener('cart:drawer-close', this.#handleBroadcastClose);
    this.#historyAbortController?.abort();

    const { dialog } = /** @type {Refs} */ (this.refs);
    dialog?.removeEventListener('close', this.#handleDialogClose);
  }

  // ─── History API (back button on mobile) ──────────────────

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  // ─── Cart page redirect ───────────────────────────────────

  /**
   * Reads and clears the reopen flag left by the cart page redirect.
   * @returns {boolean}
   */
  #consumeReopenFlag() {
    try {
      if (sessionStorage.getItem('ax:cart-drawer-reopen') === null) return false;
      sessionStorage.removeItem('ax:cart-drawer-reopen');
      return true;
    } catch {
      return false;
    }
  }

  // ─── Cart Add ─────────────────────────────────────────────

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {Event & { detail?: { resource?: { item_count?: number }, data?: { source?: string, didError?: boolean } } }} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    const data = event.detail?.data;

    // Don't open drawer on failed add-to-cart (out of stock, etc.)
    if (data?.didError) return;

    // ONLY auto-open on a real user "add to cart" action — i.e. `cart:update`
    // dispatched by product-form-component (ATC click) or pdp-bundle-store
    // (bundle commit). Every other source represents passive cart state
    // syncing and should NOT pop the drawer open:
    //   - `cart-manager`        → third-party cart fetch detected by the
    //                             HTTP interceptor. The initial /cart sync
    //                             on page load was opening the drawer for
    //                             no reason.
    //   - `cart-broadcast`      → another tab opened/closed the drawer
    //   - `cart-items-component`→ qty / remove update inside the drawer
    // Whitelist user-initiated sources only — safer than blacklisting.
    if (data?.source !== 'product-form-component' && data?.source !== 'pdp-bundle-store') return;

    // Defer one frame so a concurrent section morph can replace this instance
    // before we open the dialog — redirect to the live cart-drawer if so.
    requestAnimationFrame(() => {
      const current =
        this.isConnected ? this : document.querySelector('cart-drawer-component');
      if (!(current instanceof CartDrawerComponent)) return;
      if (!current.hasAttribute('auto-open')) return;
      if (current.refs?.dialog?.open) return;
      current.open();
      current.#announceCartCount(event.detail?.resource?.item_count);
    });
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  // ─── Broadcast sync (multi-tab) ───────────────────────────

  /**
   * Dispatches a cart:drawer-close event when the dialog closes,
   * so cart-broadcast can sync the close to other tabs.
   * Skips dispatching if the close was triggered by a broadcast from another tab.
   * When on /cart with redirect enabled, navigates to home.
   */
  #handleDialogClose = () => {
    if (this.#closingFromBroadcast) {
      this.#closingFromBroadcast = false;
      return;
    }

    document.dispatchEvent(
      new CustomEvent('cart:drawer-close', { bubbles: true })
    );

    if (this.hasAttribute('data-redirect-cart-page') && window.location.pathname === `${window.Shopify?.routes?.root ?? '/'}cart`) {
      window.location.href = window.Shopify?.routes?.root ?? '/';
    }
  };

  /**
   * Handles close events received from other tabs via cart-broadcast.
   * @param {Event & { detail?: { source?: string } }} event
   */
  #handleBroadcastClose = (event) => {
    if (event.detail?.source !== 'cart-broadcast') return;

    this.#closingFromBroadcast = true;
    this.closeDialog();
  };

  // ─── Open / Close ─────────────────────────────────────────

  open() {
    const perfOp = axPerfLogger.start('drawer-open', { triggeredBy: 'manual' });
    this.showDialog();
    perfOp.mark('dialog-shown');
    this.#dispatchOpenEvent();
    perfOp.mark('open-event-dispatched');
    this.#checkLazyLoadImages();
    perfOp.mark('lazy-check-done');
    perfOp.end();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ─── Lazy load images ─────────────────────────────────────

  /**
   * Dispatches removeLazyKeep event for images with data-lazy-keep inside the drawer,
   * so observer-CFH can load them now that the drawer is visible.
   */
  #checkLazyLoadImages() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || dialog.querySelectorAll('[data-lazy-keep]').length === 0) return;

    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', {
        detail: { lazyParentElement: dialog },
      })
    );
  }

  // ─── GTM Events ───────────────────────────────────────────

  /**
   * Dispatches a GTM-compatible event when the cart drawer opens.
   */
  #dispatchOpenEvent() {
    document.dispatchEvent(
      new CustomEvent('ax:cart-drawer-opened', {
        detail: { source: 'cart-drawer' },
        bubbles: true,
      })
    );
  }

  // ─── Sticky summary ───────────────────────────────────────

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
